import React, { useCallback, useEffect, useState } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import { PreviewRateUnitID } from './PreviewRateUnitID';
import { PreviewRateAccommodationId } from './PreviewRateAccommodationId';
import {
  createPricing,
  getAllAccommodations,
  getAllAccommodationsByProject,
  getAllBlocks,
  getPlc,
  getPlcbyProjectId,
  getAllPricingByProjectId
} from '../../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import Select from 'react-select';
import mixpanel from 'mixpanel-browser';

export const CreateRateList = ({
  projectName,
  createSuccess,
  pricingType,
  onClose,
  initialSubCategory = ''
}) => {
  const userId = Cookies.get('userId');
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');

  const [selectedAccommodationType, setSelectedAccommodationType] = useState('GROUP');
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const [subCategory, setSubCategory] = useState(''); // '' | 'accommodation' | measurementType (e.g. 'sqft')
  const [onProccedbtn, setProccedbtn] = useState('');
  const { id: projectId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accommodationListOptions, setAccommodationListOptions] = useState([]);
  const [blocksListOptions, setBlocksListOptions] = useState([]);
  const [unitListOptions, setUnitListOptions] = useState([]);
  const [PLCListOptions, setPLCListOptions] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState('');

  //Preview States
  const [formPayload, setFormPayload] = useState({});
  const [previewData, setPreviewData] = useState([]);

  // API Calls
  const { data: accommodations, refetch: refetchAccommodations } = useQuery({
    queryKey: ['accommodations', projectId, selectedAccommodationType],
    queryFn: () =>
      getAllAccommodationsByProject(projectId, `&accomodationType=${selectedAccommodationType}`),
    enabled: !!projectId && !!selectedAccommodationType
  });

  // Get all pricing data to check which accommodations already have pricing
  const { data: getAllPricingList } = useQuery({
    queryKey: ['getAllPricingList', projectId],
    queryFn: () => getAllPricingByProjectId(projectId),
    enabled: !!projectId
  });

  const { data: blocksDyna, refetch: refetchBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const { data: PLCbyProjectIdList, refetch: refetchPLCProject } = useQuery({
    queryKey: ['PLCbyProjectIdList'],
    queryFn: () => getPlc(),
    enabled: !!projectId
  });

  useEffect(() => {
    setSubCategory(initialSubCategory || '');
    setSelectedAccommodation(null);
  }, [initialSubCategory]);

  // Track page view
  useEffect(() => {
    mixpanel.track('Create Rate List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (accommodations && getAllPricingList) {
      const accommodationNameList = Object.values(accommodations).flat();
      if (accommodationNameList.length > 0) {
        // Accommodations that already have ANY rate list (sqft or accommodation) for this accommodation type
        const accommodationsWithPricing = new Set(
          getAllPricingList
            .filter(
              (pricing) => pricing.accommodation?.accomodationType === selectedAccommodationType
            )
            .map((pricing) => pricing.accommodation_id)
        );

        // Only show accommodations that have no rate list yet (same list for both rate types)
        const availableAccommodations = accommodationNameList.filter(
          (item) =>
            !accommodationsWithPricing.has(item.id) &&
            item.accomodationType === selectedAccommodationType
        );

        setAccommodationListOptions(
          availableAccommodations.map((item) => ({
            value: item.id,
            label: item.name,
            type: item.accomodationType,
            buildUpArea: item.buildUpArea ?? item.build_up_area ?? '',
            plotArea: item.plotArea ?? item.plot_area ?? ''
          }))
        );
      } else {
        setAccommodationListOptions([]);
      }
    } else {
      setAccommodationListOptions([]);
    }
  }, [accommodations, getAllPricingList, selectedAccommodationType, subCategory]);

  useEffect(() => {
    if (blocksDyna && blocksDyna.blocks) {
      setBlocksListOptions([
        { value: 'Select type', label: 'Select type' },
        ...blocksDyna.blocks.map((item) => ({ value: item.id, label: item.name }))
      ]);
    }
  }, [blocksDyna]);

  useEffect(() => {
    if (PLCbyProjectIdList) {
      const filterList = PLCbyProjectIdList.filter((item) => item.id === parseInt(selectedBlockId));
      const extractPLC = filterList.flatMap((item) => item.plcs);
      const PLCData = extractPLC?.map((plc) => ({ value: plc.id, label: plc.name }));
      setPLCListOptions([{ value: 'Select type', label: 'Select type' }, ...PLCData]);
    }
  }, [PLCbyProjectIdList, selectedBlockId]);

  useEffect(() => {
    if (blocksDyna && blocksDyna.blocks) {
      const filteredBlocks = blocksDyna.blocks.filter(
        (item) => item.id === parseInt(selectedBlockId)
      );
      const extractedUnits = filteredBlocks.flatMap((item) =>
        item.levels.flatMap((level) => level.units)
      );
      const unitsData = extractedUnits?.map((unit) => ({
        value: unit.id,
        label: unit.unitName
      }));
      setUnitListOptions([{ value: 'Select type', label: 'Select type' }, ...unitsData]);
    }
  }, [blocksDyna, selectedBlockId]);

  const handlePreviewAccmmodationIDonProccedbtn = () => {
    setProccedbtn('previewAccommodationById');
    setSubCategory('');
  };

  const handlePreviewUnitIDonProccedbtn = () => {
    setProccedbtn('previewUnitById');
    setSubCategory('');
  };

  const handleAccommodationTypeChange = (e) => {
    setSelectedAccommodationType(e.target.value);
    setSelectedAccommodation(null);
  };

  const handleAccommodationChange = (selected) => {
    setSelectedAccommodation(selected || null);
  };


  const handleRateTypeChange = (e) => {
    setSubCategory(e.target.value);
    setSelectedAccommodation(null);
  };

  const onSubmit = (data) => {
    if (!selectedAccommodation) return;
    setPreviewData([]);

    const accommodation = selectedAccommodation;
    const basePayload = {
      mode: subCategory === 'sqft' || subCategory === 'sqmt' ? 'sqft' : 'accommodation',
      accommodation_id: accommodation.value,
      project_id: parseInt(projectId)
    };

    if (subCategory === measurementTypeWithoutSlash) {
      basePayload.loading_factor = parseFloat(data.loadingFactor) || 0;

      if (selectedAccommodationType === 'INDEPENDENT') {
        basePayload.plote_price = parseInt(data.plote_price) || 0;
        basePayload.construction_price = parseInt(data.construction_price) || 0;
      } else {
        basePayload.price = parseInt(data.rate_per_sqft) || 0;
      }
    } else if (subCategory === 'accommodation') {
      if (selectedAccommodationType === 'INDEPENDENT') {
        basePayload.plote_price = parseInt(data.plote_price) || 0;
        basePayload.construction_price = parseInt(data.construction_price) || 0;
      } else {
        basePayload.price = parseInt(data.price) || 0;
      }
    }

    const payloads = [basePayload];

    const previewPayload = {
      project_name: projectName || '',
      accommodation_name: accommodation.label,
      mode: subCategory === 'sqft' || subCategory === 'sqmt' ? 'sqft' : 'accommodation'
    };

    if (subCategory === measurementTypeWithoutSlash) {
      previewPayload.loading_factor = data.loadingFactor;
    }

    if (selectedAccommodationType === 'INDEPENDENT') {
      previewPayload.plote_price = data.plote_price;
      previewPayload.construction_price = data.construction_price;
    } else {
      previewPayload.price =
        subCategory === measurementTypeWithoutSlash ? data.rate_per_sqft : data.price;
    }

    setFormPayload({ data: payloads });
    setPreviewData([previewPayload]);

    handleCreatePricing({ data: payloads });
  };

  const { mutate: handleCreatePricing } = useMutation({
    mutationFn: (payload) => createPricing(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (data) => {
      setIsSubmitting(false);
      toast.success('Pricing created successfully');
      setProccedbtn('');
      setFormPayload({});
      setPreviewData([]);
      createSuccess();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating pricing', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating pricing');
    }
  });

  const handleSelectBlockChange = (e) => {
    setSelectedBlockId(e.target.value);
  };

  const handleCreatePricingClick = () => {
    handleCreatePricing(formPayload);
  };

  const [loadingFactor, setLoadingFactor] = useState('');

  const handleLoadingFactorChange = (e) => {
    let inputVal = e;

    // 1) Remove anything that's not a digit:
    inputVal = inputVal.replace(/\D/g, '');

    // 2) Convert to a number and cap at 100:
    const numericVal = parseInt(inputVal, 10);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Loading factor cannot exceed 100');
    }

    // 3) Set our local state and also setValue in RHF so the form sees this updated value.
    setLoadingFactor(inputVal);
    setValue('loadingFactor', inputVal);
  };

  return (
    <div>
      {onProccedbtn === 'previewAccommodationById' && (
        <PreviewRateAccommodationId
          previewData={previewData}
          isSubmitting={isSubmitting}
          handlePreviewProceed={handleCreatePricingClick}
          onEdit={() => {
            setProccedbtn('');
            setSubCategory('accommodation');
          }}
        />
      )}
      {onProccedbtn === 'previewUnitById' && (
        <PreviewRateUnitID
          previewData={previewData}
          isSubmitting={isSubmitting}
          handlePreviewProceed={handleCreatePricingClick}
          onEdit={() => {
            setProccedbtn('');
            setSubCategory(measurementTypeWithoutSlash);
          }}
        />
      )}
      {!onProccedbtn && (
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="flex flex-col justify-between items-start gap-4">
            <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
              Rate Details
            </span>
          </div>

          {/* Rate Type, Accommodation Type, Accommodation Name - one row */}
          <div className="flex gap-4 mt-4 items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate Type</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={subCategory}
                onChange={handleRateTypeChange}>
                <option value="">Select rate type</option>
                <option value={measurementTypeWithoutSlash}>{measurementType} Based</option>
                <option value="accommodation">Accommodation Based</option>
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Type
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={selectedAccommodationType}
                onChange={handleAccommodationTypeChange}>
                <option value="GROUP">GROUP</option>
                <option value="INDEPENDENT">INDEPENDENT</option>
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Name
              </label>
              <Select
                options={accommodationListOptions}
                value={selectedAccommodation}
                onChange={handleAccommodationChange}
                isClearable
                placeholder={
                  accommodationListOptions.length === 0
                    ? 'No available accommodations'
                    : 'Select accommodation'
                }
                noOptionsMessage={() => 'No available accommodations. All have rate lists already.'}
                className="basic-select"
                classNamePrefix="select"
              />
            </div>
          </div>

          {/* Show price/rate inputs and buttons only after rate type is selected */}
          {subCategory && (
            <>
              {/* Build up area & Plot area - shown after rate type, read-only, auto-filled when accommodation is selected */}
              <div className="flex gap-4 mt-4 items-end">
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Build up area</label>
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {selectedAccommodation
                      ? (selectedAccommodation.buildUpArea ?? selectedAccommodation.build_up_area ?? '—')
                      : '—'}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plot area</label>
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {selectedAccommodation
                      ? (selectedAccommodation.plotArea ?? selectedAccommodation.plot_area ?? '—')
                      : '—'}
                  </div>
                </div>
              </div>

              {subCategory === measurementTypeWithoutSlash && (
                <div className="flex justify-between mt-4">
                  <FormInput
                    width={49}
                    label={'Loading Factor (%)'}
                    register_key={'loadingFactor'}
                    register={register}
                    type={'text'}
                    onType={handleLoadingFactorChange}
                    defaultValuePre={loadingFactor}
                    errors={errors.loadingFactor}
                  />
                </div>
              )}

              <div className="flex w-full justify-between mt-4">
                {selectedAccommodationType === 'INDEPENDENT' ? (
                  <div className="flex justify-between mt-4 w-full">
                    <FormInput
                      width={49}
                      label={'Plot Price'}
                      register_key={'plote_price'}
                      register={register}
                      isRequired={true}
                      type={'text'}
                    />
                    <FormInput
                      width={49}
                      label={'Construction Price'}
                      register_key={'construction_price'}
                      register={register}
                      type={'text'}
                    />
                  </div>
                ) : (
                  <FormInput
                    width={49}
                    label={
                      subCategory === measurementTypeWithoutSlash
                        ? `Rate per ${measurementType}`
                        : 'Price'
                    }
                    register_key={
                      subCategory === measurementTypeWithoutSlash ? 'rate_per_sqft' : 'price'
                    }
                    isRequired={true}
                    register={register}
                    type={'text'}
                  />
                )}
              </div>

              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button type="reset" displayType="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!selectedAccommodation}>
                  Proceed
                </Button>
              </div>
            </>
          )}
        </div>
      </form>
      )}
    </div>
  );
};
